import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as Menu from "@radix-ui/react-dropdown-menu";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { create } from "zustand";
import { Button, cn } from "./core";

// ── Dialog ──────────────────────────────────────────────────────────────────
// A centred dialog; on a phone, a sheet that slides up from the bottom with a
// grab handle, room for the home bar, and full-width buttons (main action on top).
const handle = <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-line-strong sm:hidden" aria-hidden />;
const footerCls =
  "flex shrink-0 flex-col-reverse gap-2 border-t border-line px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:flex-row sm:justify-end sm:px-5 sm:pb-3 [&>*]:max-sm:w-full [&>*]:max-sm:h-11";

export function Dialog({ open, onOpenChange, title, description, children, footer, size = "md" }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
}) {
  const w = { sm: "sm:max-w-sm", md: "sm:max-w-lg", lg: "sm:max-w-2xl", xl: "sm:max-w-4xl" }[size];
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="anim-fade fixed inset-0 z-50 bg-black/55 backdrop-blur-[2px]" />
        <DialogPrimitive.Content
          className={cn(
            "fixed z-50 flex flex-col border-line bg-panel shadow-2xl focus:outline-none",
            "inset-x-0 bottom-0 max-h-[92dvh] rounded-t-2xl border-t max-sm:anim-sheet-up",
            "sm:inset-x-auto sm:top-1/2 sm:bottom-auto sm:left-1/2 sm:max-h-[min(88vh,900px)] sm:w-[calc(100vw-2rem)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl sm:border sm:anim-pop",
            w,
          )}
        >
          {handle}
          <div className="flex items-start gap-3 border-b border-line px-4 py-3 sm:px-5 sm:py-3.5">
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="text-[16px] font-semibold tracking-tight sm:text-[14.5px]">{title}</DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-0.5 text-[12.5px] text-muted">{description}</DialogPrimitive.Description>
              ) : (
                <DialogPrimitive.Description className="sr-only">{typeof title === "string" ? title : "Dialog"}</DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Close" className="max-sm:size-9">
                <X className="size-4" />
              </Button>
            </DialogPrimitive.Close>
          </div>
          {children && <div className={cn("min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5", !footer && "pb-[calc(1rem+env(safe-area-inset-bottom))] sm:pb-4")}>{children}</div>}
          {footer && <div className={footerCls}>{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// ── Sheet (side drawer; a bottom sheet on a phone) ──────────────────────────
export function Sheet({ open, onOpenChange, title, description, children, footer, side = "right", width = "max-w-xl" }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  side?: "right" | "left" | "bottom";
  width?: string;
}) {
  const desktop =
    side === "right"
      ? cn("sm:inset-x-auto sm:top-0 sm:right-0 sm:bottom-auto sm:h-full sm:max-h-none sm:w-full sm:rounded-none sm:border-t-0 sm:border-l sm:anim-fade", width.replace(/(^|\s)max-w/g, "$1sm:max-w"))
      : side === "left"
        ? "sm:inset-x-auto sm:top-0 sm:left-0 sm:bottom-auto sm:h-full sm:max-h-none sm:w-[min(86vw,300px)] sm:rounded-none sm:border-t-0 sm:border-r sm:anim-fade"
        : "sm:mx-auto sm:max-w-xl";
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="anim-fade fixed inset-0 z-50 bg-black/50" />
        <DialogPrimitive.Content
          className={cn(
            "fixed z-50 flex flex-col border-line bg-panel shadow-2xl focus:outline-none",
            "inset-x-0 bottom-0 max-h-[92dvh] w-full rounded-t-2xl border-t",
            side === "bottom" ? "anim-sheet-up" : "max-sm:anim-sheet-up",
            desktop,
          )}
        >
          {side === "bottom" ? <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-line-strong" aria-hidden /> : handle}
          <div className="flex items-start gap-3 border-b border-line px-4 py-3 sm:px-5 sm:py-3.5">
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="truncate text-[16px] font-semibold sm:text-[14.5px]">{title}</DialogPrimitive.Title>
              <DialogPrimitive.Description className={description ? "mt-0.5 text-[12.5px] text-muted" : "sr-only"}>
                {description ?? "Details"}
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Close" className="max-sm:size-9">
                <X className="size-4" />
              </Button>
            </DialogPrimitive.Close>
          </div>
          <div className={cn("min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5", !footer && "pb-[calc(1rem+env(safe-area-inset-bottom))]")}>{children}</div>
          {footer && <div className={footerCls}>{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// ── Tooltip / Popover / Menu ────────────────────────────────────────────────
export function Tip({ content, children, side = "top" }: { content: ReactNode; children: ReactNode; side?: "top" | "bottom" | "left" | "right" }) {
  if (!content) return <>{children}</>;
  return (
    <TooltipPrimitive.Root delayDuration={250}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content side={side} sideOffset={6} className="z-[60] max-w-xs rounded-md border border-line bg-panel-3 px-2 py-1.5 text-[11.5px] text-fg shadow-lg">
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export const TooltipProvider = TooltipPrimitive.Provider;

export function Popover({ trigger, children, align = "start", className, open, onOpenChange }: {
  trigger: ReactNode;
  children: ReactNode;
  align?: "start" | "center" | "end";
  className?: string;
  open?: boolean;
  onOpenChange?: (o: boolean) => void;
}) {
  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content align={align} sideOffset={6} collisionPadding={8} className={cn("z-50 max-w-[calc(100vw-1rem)] rounded-xl border border-line bg-panel p-2 shadow-2xl focus:outline-none", className)}>
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export function DropMenu({ trigger, items, align = "end" }: {
  trigger: ReactNode;
  items: ({ label: ReactNode; onSelect: () => void; danger?: boolean; icon?: ReactNode; disabled?: boolean } | "sep")[];
  align?: "start" | "end";
}) {
  return (
    <Menu.Root>
      <Menu.Trigger asChild>{trigger}</Menu.Trigger>
      <Menu.Portal>
        <Menu.Content align={align} sideOffset={6} collisionPadding={8} className="z-50 max-h-[min(70dvh,560px)] min-w-44 overflow-y-auto rounded-xl border border-line bg-panel p-1 shadow-2xl">
          {items.map((it, i) =>
            it === "sep" ? (
              <Menu.Separator key={i} className="my-1 h-px bg-line" />
            ) : (
              <Menu.Item
                key={i}
                disabled={it.disabled}
                onSelect={it.onSelect}
                className={cn(
                  "flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-[12.5px] outline-none pointer-coarse:py-2.5 pointer-coarse:text-[14px] data-[disabled]:opacity-40 data-[highlighted]:bg-panel-3",
                  it.danger && "text-down",
                )}
              >
                {it.icon}
                {it.label}
              </Menu.Item>
            ),
          )}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}

// ── Confirm: a promise-returning confirmation dialog ────────────────────────
interface ConfirmReq {
  title: string;
  body?: ReactNode;
  confirm?: string;
  danger?: boolean;
  requireText?: string; // type this to enable the button (for destructive actions)
}

interface ConfirmState {
  req: ConfirmReq | null;
  resolve: ((ok: boolean) => void) | null;
  ask: (r: ConfirmReq) => Promise<boolean>;
  done: (ok: boolean) => void;
}

const useConfirmStore = create<ConfirmState>((set, get) => ({
  req: null,
  resolve: null,
  ask: (req) =>
    new Promise((resolve) => {
      get().resolve?.(false);
      set({ req, resolve });
    }),
  done: (ok) => {
    get().resolve?.(ok);
    set({ req: null, resolve: null });
  },
}));

export const confirm = (r: ConfirmReq) => useConfirmStore.getState().ask(r);

export function ConfirmHost() {
  const { req, done } = useConfirmStore();
  const [typed, setTyped] = useState("");
  const blocked = !!req?.requireText && typed.trim().toUpperCase() !== req.requireText.toUpperCase();
  return (
    <Dialog
      open={!!req}
      onOpenChange={(o) => {
        if (!o) {
          setTyped("");
          done(false);
        }
      }}
      size="sm"
      title={req?.title ?? ""}
      footer={
        <>
          <Button variant="ghost" onClick={() => { setTyped(""); done(false); }}>
            Cancel
          </Button>
          <Button
            variant={req?.danger ? "danger" : "primary"}
            disabled={blocked}
            onClick={() => {
              setTyped("");
              done(true);
            }}
          >
            {req?.confirm ?? "Confirm"}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-[13px] text-fg/85">
        {req?.body}
        {req?.requireText && (
          <label className="block space-y-1.5">
            <span className="text-[12px] text-muted">
              Type <b className="font-mono text-fg">{req.requireText}</b> to continue
            </span>
            <input
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="h-9 w-full rounded-lg border border-line bg-panel-2 px-2.5 font-mono text-[13px] focus:border-accent focus:outline-none"
            />
          </label>
        )}
      </div>
    </Dialog>
  );
}
